import { queryKeys } from '@query/keys'
import { PaginatedQueryResult, usePaginatedQuery } from '@query/usePaginatedQuery'
import { useMemo, useState } from 'react'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'
import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '@model/NameDto'

interface IBRebateGroupsSearch {
  groupOptions: NameDto<string>[]
  query: PaginatedQueryResult<IntroducingBrokerRebateGroupDto>
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  clearSearch: (callback: () => void) => void
}

interface IBRebateGroupsSearchProps {
  query?: PageQuery
}

export const useIBRebateGroupsSearch = ({
  query: providedQuery,
}: IBRebateGroupsSearchProps = {}): IBRebateGroupsSearch => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const ibId = account?.clientIntroducingBroker?.id

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.rebates.groupsSearch(ibId, { search, locale } as any),
    queryFn: (pageQuery) =>
      apiClient.getIBRebateGroups(ibId ?? '', {
        pageIndex: 0,
        pageSize: 25,
        caller: 'ca',
        languageId: locale,
        ...providedQuery,
        ...pageQuery,
        search,
      }),
  })

  const groupOptions = useMemo<NameDto<string>[]>(() => {
    return query.data.map((group) => ({
      id: group.id,
      name: group.name || group.id,
    }))
  }, [query.data])

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  return {
    groupOptions,
    query,
    search,
    setSearch,
    clearSearch,
  }
}
